import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { preloadAutoFollowSpacesState } from '@confluence/onboarding-helpers/entry-points/preloadAutoFollowSpacesState.ts';
import { preloadFeed } from '@confluence/feed/entry-points/preloadFeed.ts';
import { preloadPostOfficeConfluenceHomeBanner } from '@confluence/experiment-post-office-home-banner/entry-points/preloadConfuenceHomeBanner.ts';
import { preloadPersonalizedTemplates } from '@confluence/experiment-personalized-templates-v2/entry-points/preloadPersonalizedTemplates.ts';
import { preloadNthUserGettingStartedOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadNthUserGettingStartedOnboardingState.ts';
import { preloadUseCasesNthUserOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadUseCasesNthUserOnboardingState.ts';
import { preloadHomeOnboardingTakeoverV2 } from '@confluence/onboarding-helpers/entry-points/preloadHomeOnboardingTakeoverV2.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { preloadPersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/preloadPersistentInviteEligible.ts';
import { fg } from '@confluence/feature-gating';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';

import { preloadTemplatesCollectionEligible } from '../../../../entry-points/preloadTemplatesCollectionEligible.ts';
import { preloadIntentBasedOnboarding } from '../../../../entry-points/preloadIntentBasedOnboarding.ts';
import { preloadSidePanel } from '../../preloadSidePanel.ts';
import { preloadWelcomeMessages } from '../../RightPanel/WelcomeMessage/preloadWelcomeMessages.ts';
import { shouldUseAGGActivityV3 } from '../shouldUseAGGActivityV3.ts';

import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers.ts';
import { ALL_RECENT_ITEMS_COUNT_HOME_REFRESH } from './recentListConstants.ts';

const preloadRecentWork = (args: {
	cloudId: string;
	isLoggedIn: boolean;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	isSlidesEnabled?: boolean;
	first?: number;
}) => {
	const queryOptions = getRecentWorkQueryOptions(args);

	if (queryOptions.skip) {
		return Promise.resolve({});
	}
	return (shouldUseAGGActivityV3() ? queryAgg : query)(queryOptions);
};

export const preloadForYou = ({
	userId,
	cloudId,
	isLicensed,
	isWhiteboardViewedEnabled = false,
	isDatabaseEnabled = false,
	isSlidesEnabled = false,
}: {
	userId: string | null;
	cloudId: string;
	isLicensed: boolean;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	isSlidesEnabled?: boolean;
}) => {
	const preloadedSessionContext = getPreloaderFnContext();
	const { accessStatus } = preloadedSessionContext;
	const canAccessHome =
		accessStatus !== 'ANONYMOUS_ACCESS' && accessStatus !== 'UNLICENSED_AUTHENTICATED_ACCESS';

	const tasks = [
		preloadRecentWork({
			cloudId,
			isLoggedIn: !!userId,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			isSlidesEnabled,
			first: ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
		}),
		preloadFeed({
			isLoggedIn: !!userId,
		}),
		preloadSidePanel({
			isLicensed,
		}),
		preloadWelcomeMessages(),
	];

	if (userId) {
		tasks.push(preloadIntentBasedOnboarding(), preloadTemplatesCollectionEligible());

		if (isLicensed && canAccessHome) {
			if (fg('cc-engagement-auto-follow-recommended-spaces-gate')) {
				tasks.push(preloadAutoFollowSpacesState());
			}

			if (fg('cc-engagement-getting-started-nth-user-gate')) {
				tasks.push(preloadNthUserGettingStartedOnboardingState());
			}

			tasks.push(preloadHomeOnboardingTakeoverV2());
		}
	}

	if (isLicensed && canAccessHome && userId && fg('cc-ssr-preload-use-cases-nth-user-onboarding')) {
		tasks.push(preloadUseCasesNthUserOnboardingState());
	}

	if (isLicensed && canAccessHome && userId && accessStatus) {
		tasks.push(preloadPersonalizedTemplates(userId, accessStatus));
	}

	const unifiedSpacePreloader = preloadPersistentInviteButtonEligible(
		{ spaceKey: undefined },
		false,
	);

	if (unifiedSpacePreloader) {
		// @ts-ignore-next-line
		tasks.push(unifiedSpacePreloader); //type can be undefined, but the surrounding conditional prevents it
	}

	// https://switcheroo.atlassian.com/ui/gates/e8661ed8-caef-4eed-a747-bf4d58be15c9/key/post-office_enable-confluence-home-banner
	if (userId && canAccessHome && isLicensed && fg('post-office_enable-confluence-home-banner')) {
		// Confluence Home Banner in SSR
		const preloadPostOfficeHomeBanner = async () => {
			return SSRMeasures.run(
				'ssr-app/prepare/preloadQuery/postOffice:ConfluenceHomeBanner',
				async () => {
					const result = await preloadPostOfficeConfluenceHomeBanner(preloadedSessionContext);
					return result ?? {};
				},
			);
		};

		tasks.push(preloadPostOfficeHomeBanner());
	}

	return waitForAllReturnWhenError(tasks);
};
